/**
 * <postcode-checker> — unified PDP postcode checker.
 * Branches on data-fulfillment-type:
 * - "standard": nearest store + distance-tiered shipping fee + try-in-store/pickup status
 *   (uses window.omfGeo, assets/geolocation.js)
 * - "dropship": real Cloudflare Worker serviceability/fee check, no store section
 *
 * Replaces the old theme's separate stock-availability-widget-legacy.js and
 * dropship-shipping-estimator.js controllers.
 */

if (!customElements.get('postcode-checker')) {
function titleCase(str) {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

class PostcodeChecker extends HTMLElement {
  // Setup lives in connectedCallback (not the constructor) because this
  // theme's variant-change handling can tear down and reinsert a block's
  // markup (including its <script> data blobs) without necessarily creating
  // a brand-new element — connectedCallback reliably re-fires every time the
  // element (re)connects, so state and data always get refreshed correctly.
  connectedCallback() {
    this.fulfillmentType = this.dataset.fulfillmentType;
    this.form = this.querySelector('.postcode-checker__form');
    this.input = this.querySelector('.postcode-checker__input');
    this.submitButton = this.querySelector('.postcode-checker__submit');
    this.results = this.querySelector('[data-results]');
    this.suburbList = null;
    this.errorEl = null;
    this.currentSuburbLabel = '';
    this.currentVariantId = parseInt(this.dataset.selectedVariantId, 10);
    this.isMadeToOrder = this.dataset.madeToOrder === 'true';
    this.lastStore = null;
    this.lastStandard = null;

    if (this.fulfillmentType === 'dropship') {
      const dataEl = document.getElementById(this.dataset.variantDataId);
      if (dataEl) {
        try {
          const parsed = JSON.parse(dataEl.textContent);
          this.variants = parsed.variants;
          this.workerUrl = parsed.workerUrl;
        } catch (e) {
          console.error('Postcode checker: failed to parse variant data', e);
        }
      }
    } else if (this.fulfillmentType === 'standard') {
      const regionalEl = document.getElementById(this.dataset.regionalDataId);
      if (regionalEl) {
        try {
          this.regionalVariants = JSON.parse(regionalEl.textContent).variants;
        } catch (e) {
          console.error('Postcode checker: failed to parse regional stock data', e);
        }
      }
    }

    if (!this.form || !this.input || !this.submitButton || !this.results) return;

    this.onSubmitBound = this.onSubmit.bind(this);
    this.onInputBound = () => {
      this.clearError();
      this.removeSuburbList();
      this.hideResults();
    };

    this.form.addEventListener('submit', this.onSubmitBound);
    this.input.addEventListener('input', this.onInputBound);

    this.subscribeToVariantChange();
  }

  disconnectedCallback() {
    if (this.form && this.onSubmitBound) this.form.removeEventListener('submit', this.onSubmitBound);
    if (this.input && this.onInputBound) this.input.removeEventListener('input', this.onInputBound);
    if (this.unsubscribeVariantChange) this.unsubscribeVariantChange();
  }

  subscribeToVariantChange() {
    if (typeof FoxTheme === 'undefined' || !FoxTheme.pubsub) return;

    this.unsubscribeVariantChange = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
      (event) => {
        if (event.data.sectionId !== this.dataset.sectionId) return;
        if (!event.data.variant) return;

        this.currentVariantId = event.data.variant.id;

        if (this.fulfillmentType === 'dropship') {
          const postcode = this.getEnteredPostcode();
          if (postcode) this.checkDropship(postcode, this.currentSuburbLabel);
        } else if (this.fulfillmentType === 'standard' && this.lastStore) {
          // The variant may or may not be BiB, so the delivery fee can change too
          if (this.lastStandard) {
            const { store, distance, suburb, deliveryExcluded } = this.lastStandard;
            this.renderStandardResults(store, this.getStandardShipping(store, distance), suburb, deliveryExcluded);
          }
          this.renderRegionalStock(this.lastStore);
        }
      }
    );
  }

  getEnteredPostcode() {
    const digits = this.input.value.replace(/\D/g, '').slice(0, 4);
    return /^\d{4}$/.test(digits) ? digits : null;
  }

  async onSubmit(e) {
    e.preventDefault();
    this.clearError();
    this.removeSuburbList();

    const postcode = this.input.value.trim().replace(/\D/g, '').slice(0, 4);

    if (!/^\d{4}$/.test(postcode)) {
      this.showError('Please enter a valid 4-digit postcode');
      return;
    }

    this.submitButton.disabled = true;

    try {
      if (!window.omfGeo) throw new Error('Location service unavailable. Please refresh and try again.');

      const matches = await window.omfGeo.getPostcodeMatches(postcode);

      if (!matches || matches.length === 0) {
        this.showError('Postcode not found. Please check and try again.');
        return;
      }

      if (matches.length === 1) {
        this.selectSuburb(matches[0]);
      } else {
        this.showSuburbList(matches);
      }
    } catch (err) {
      this.showError(err.message || 'Something went wrong. Please try again.');
    } finally {
      this.submitButton.disabled = false;
    }
  }

  // ── Suburb disambiguation ──────────────────────────────────────────────

  showSuburbList(matches) {
    this.removeSuburbList();

    const list = document.createElement('div');
    list.className = 'postcode-checker__suburb-list';

    matches.forEach((match) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'postcode-checker__suburb-item';

      const suburbSpan = document.createElement('span');
      suburbSpan.textContent = titleCase(match.Suburb);

      const stateSpan = document.createElement('span');
      stateSpan.className = 'postcode-checker__suburb-item-state';
      stateSpan.textContent = `${match.State} ${match.Postocode}`;

      item.append(suburbSpan, stateSpan);
      item.addEventListener('click', () => {
        this.input.value = `${titleCase(match.Suburb)}, ${match.State} ${match.Postocode}`;
        this.removeSuburbList();
        this.selectSuburb(match);
      });

      list.appendChild(item);
    });

    this.form.after(list);
    this.suburbList = list;
  }

  removeSuburbList() {
    if (this.suburbList) {
      this.suburbList.remove();
      this.suburbList = null;
    }
  }

  selectSuburb(suburb) {
    this.currentSuburbLabel = titleCase(suburb.Suburb);
    if (this.fulfillmentType === 'standard') {
      this.checkStandard(suburb);
    } else {
      this.checkDropship(suburb.Postocode, this.currentSuburbLabel);
    }
  }

  // ── Shared UI helpers ────────────────────────────────────────────────────

  setOption(name, available, text) {
    const option = this.querySelector(`[data-option="${name}"]`);
    if (!option) return;
    const dot = option.querySelector('[data-dot]');
    const title = option.querySelector('[data-title]');
    if (dot) dot.classList.toggle('is-available', !!available);
    if (title && text !== undefined) title.textContent = text;
  }

  setLoading(on) {
    this.results.classList.toggle('is-loading', on);
  }

  showResults() {
    this.results.classList.add('is-visible');
  }

  hideResults() {
    this.results.classList.remove('is-visible');
  }

  showError(message) {
    this.clearError();
    const el = document.createElement('div');
    el.className = 'postcode-checker__error';
    el.textContent = message;
    this.form.after(el);
    this.errorEl = el;
  }

  clearError() {
    if (this.errorEl) {
      this.errorEl.remove();
      this.errorEl = null;
    }
  }

  // ── Standard path ───────────────────────────────────────────────────────

  async checkStandard(suburb) {
    this.clearError();
    this.showResults();
    this.setLoading(true);

    try {
      if (!window.omfGeo) throw new Error('Location service unavailable');

      const lat = parseFloat(suburb.Latitude);
      const lng = parseFloat(suburb.Longitude);

      // PO Box postcodes often have bad/negative coordinate data.
      if (isNaN(lat) || isNaN(lng) || lng <= 0) {
        this.setLoading(false);
        this.hideResults();
        this.showError('This appears to be a PO Box postcode. Please enter a street delivery postcode.');
        return;
      }

      const result = await window.omfGeo.getNearestStore(lat, lng);
      if (!result) throw new Error('No stores found');

      const shipping = this.getStandardShipping(result.store, result.distance);
      const vendor = this.dataset.vendor || '';
      const excluded = vendor ? await window.omfGeo.getVendorExclusions(vendor) : [];
      const deliveryExcluded = excluded.includes(suburb.Postocode);

      this.lastStandard = { store: result.store, distance: result.distance, suburb, deliveryExcluded };
      this.renderStandardResults(result.store, shipping, suburb, deliveryExcluded);
      this.lastStore = result.store;
      this.renderRegionalStock(result.store);
      this.setLoading(false);
    } catch (err) {
      console.error('Postcode checker: error checking availability', err);
      this.setLoading(false);
      this.renderStandardError();
    }
  }

  // Distance-tier fee, overridden by the BiB rate when the selected variant is BiB
  getStandardShipping(store, distance) {
    const shipping = window.omfGeo.getShippingTier(distance);
    const variantData = this.getRegionalVariantData(this.currentVariantId);
    const bib = window.omfGeo.getBibDelivery?.(store, distance, variantData?.rateCategory);
    return bib ? { ...shipping, ...bib, bib: true } : shipping;
  }

  renderStandardResults(store, shipping, suburb, deliveryExcluded) {
    const storeName = this.querySelector('[data-store-name]');
    const storeAddress = this.querySelector('[data-store-address]');
    if (storeName) {
      storeName.textContent = `OMF ${store.name}`;
      storeName.classList.remove('is-error');
    }
    if (storeAddress) storeAddress.textContent = `${store.address}, ${store.city} ${store.postcode}`.trim();

    const directionsLink = this.querySelector('[data-directions-link]');
    if (directionsLink) {
      directionsLink.href = store.pageUrl || '/pages/stores';
      directionsLink.hidden = false;
    }

    const rawPhone = store.phone || '';
    const telHref = rawPhone ? `tel:${rawPhone.replace(/\D/g, '')}` : '';
    this.querySelectorAll('[data-phone-display]').forEach((el) => {
      el.textContent = rawPhone;
    });

    const phoneLink = this.querySelector('[data-phone-link]');
    if (phoneLink) {
      phoneLink.href = telHref;
      phoneLink.hidden = !rawPhone;
    }

    const costLineRow = this.querySelector('[data-option="delivery-fee-line"]');
    const costLine = this.querySelector('[data-cost-line]');
    if (costLineRow && costLine) {
      const showCostLine = !deliveryExcluded && !!shipping;
      costLineRow.hidden = !showCostLine;
      costLine.textContent = showCostLine ? `Delivery Fee: $${shipping.rate} · ${shipping.label}` : '';
    }

    const isOmfVendor =
      this.dataset.vendor === 'Original Mattress Factory' ||
      this.dataset.vendor === 'Original Mattress Factory Australia';
    const suburbLabel = suburb ? titleCase(suburb.Suburb) : '';
    const deliveryText = [suburbLabel, suburb?.Postocode].filter(Boolean).join(' ');
    const deliveryAvailableText = deliveryText ? `Available to order to ${deliveryText}` : 'Available to order';

    this.setOption(
      'try-instore',
      isOmfVendor,
      isOmfVendor
        ? this.dataset.tryInstoreAvailable || 'Available to try in-store'
        : this.dataset.tryInstoreUnavailable || 'Not available to try in-store'
    );

    this.setOption(
      'pickup',
      isOmfVendor,
      isOmfVendor ? 'Free pickup available' : this.dataset.pickupUnavailable || 'Not available for Click & Collect'
    );

    const deliveryCallback = this.querySelector('[data-option="delivery-callback"]');

    if (deliveryExcluded) {
      this.setOption(
        'delivery',
        false,
        `Delivery unavailable to ${deliveryText} — please contact our team to discuss options`
      );
      if (deliveryCallback) deliveryCallback.hidden = true;
    } else {
      if (deliveryCallback) deliveryCallback.hidden = false;
      this.setOption('delivery', true, deliveryAvailableText);
      this.setOption(
        'delivery-callback',
        true,
        shipping?.description || 'Our team will call within 1 business day to book your delivery'
      );
    }

    const callCta = this.querySelector('[data-call-cta]');
    if (callCta) {
      if (isOmfVendor && rawPhone) {
        callCta.href = telHref;
        callCta.hidden = false;
      } else {
        callCta.hidden = true;
      }
    }
  }

  renderStandardError() {
    this.showResults();
    const storeName = this.querySelector('[data-store-name]');
    if (storeName) {
      storeName.textContent = 'Error checking availability';
      storeName.classList.add('is-error');
    }
    const directionsLink = this.querySelector('[data-directions-link]');
    if (directionsLink) directionsLink.hidden = true;
    const phoneLink = this.querySelector('[data-phone-link]');
    if (phoneLink) phoneLink.hidden = true;
    this.querySelectorAll('[data-dot]').forEach((dot) => dot.classList.remove('is-available'));
  }

  // ── Regional DC stock (standard path only) ─────────────────────────────

  resolveRegionBucket(dcRegion) {
    if (dcRegion === 'QLD') return 'qld';
    if (dcRegion === 'NSW') return 'nsw';
    return null;
  }

  getRegionalVariantData(id) {
    return (this.regionalVariants || []).find((v) => v.id === id) || null;
  }

  setRegionalStockState(state, text) {
    const row = this.querySelector('[data-option="regional-stock"]');
    if (!row) return;
    row.hidden = false;
    row.classList.toggle('is-available', state === 'available');
    row.classList.toggle('is-limited', state === 'limited');
    row.classList.toggle('is-made-to-order', state === 'made-to-order');
    row.textContent = text;
  }

  renderRegionalStock(store) {
    const row = this.querySelector('[data-option="regional-stock"]');
    if (!row) return;

    const variantData = this.getRegionalVariantData(this.currentVariantId);
    const bucket = this.resolveRegionBucket(store?.dcRegion);

    if (!variantData) {
      row.hidden = true;
      return;
    }

    const qldSet = variantData.qldStatus === true || variantData.qldStatus === false;
    const nswSet = variantData.nswStatus === true || variantData.nswStatus === false;
    const specialMessage = variantData.specialMessage || 'Limited stock · Call store for availability';
    // Made to Order products aren't actually limited stock — a false QLD/NSW
    // status just means it's produced on demand, so it gets its own green
    // "Made to Order" line instead of the orange limited-stock warning, same
    // treatment as the Availability table above this widget.
    const madeToOrderText = 'Made to Order · See lead time below';

    if (bucket) {
      const bucketLabel = bucket === 'qld' ? 'QLD' : 'NSW/VIC';
      const bucketSet = bucket === 'qld' ? qldSet : nswSet;
      const bucketValue = bucket === 'qld' ? variantData.qldStatus : variantData.nswStatus;

      if (bucketSet) {
        if (bucketValue) {
          this.setRegionalStockState('available', `${bucketLabel} · In stock · Ready to ship`);
        } else if (this.isMadeToOrder) {
          this.setRegionalStockState('made-to-order', `${bucketLabel} · ${madeToOrderText}`);
        } else {
          this.setRegionalStockState('limited', `${bucketLabel} · ${specialMessage}`);
        }
        return;
      }
    }

    if (!qldSet && !nswSet && variantData.specialMessage) {
      if (this.isMadeToOrder) {
        this.setRegionalStockState('made-to-order', madeToOrderText);
      } else {
        this.setRegionalStockState('limited', specialMessage);
      }
      return;
    }

    row.hidden = true;
  }

  // ── Dropship path ───────────────────────────────────────────────────────

  getVariantData(id) {
    return (this.variants || []).find((v) => v.id === id) || null;
  }

  async checkDropship(postcode, suburbLabel) {
    const variantData = this.getVariantData(this.currentVariantId);
    if (!variantData || !this.workerUrl) return;

    this.clearError();
    this.showResults();
    this.setLoading(true);

    try {
      const res = await fetch(this.workerUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          postcode,
          vendor: this.dataset.vendor,
          sku: variantData.sku,
          rate_category: variantData.rateCategory,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      this.renderDropshipResult(data.serviceable, data.rate, data.free, postcode, suburbLabel);
    } catch (err) {
      this.renderDropshipError();
    } finally {
      this.setLoading(false);
    }
  }

  renderDropshipResult(serviceable, rate, free, postcode, suburbLabel) {
    const feeRow = this.querySelector('[data-option="delivery-fee"]');
    const cta = this.querySelector('[data-dropship-cta]');

    if (!serviceable) {
      const location = suburbLabel ? `${suburbLabel} ${postcode}` : postcode;
      this.setOption('delivery', false, `Delivery is not available to ${location}.`);
      if (feeRow) feeRow.hidden = true;
      if (cta) {
        cta.textContent = 'Please contact your nearest store for delivery enquiries.';
        cta.hidden = false;
      }
      return;
    }

    this.setOption(
      'delivery',
      true,
      free || !rate ? 'Free delivery available to your postcode.' : 'Delivery available to your postcode.'
    );

    if (cta) {
      cta.textContent = 'Delivered directly from our supplier to your door.';
      cta.hidden = false;
    }

    if (free || !rate) {
      if (feeRow) feeRow.hidden = true;
    } else if (feeRow) {
      const dollars = (rate / 100).toFixed(2);
      this.setOption('delivery-fee', true, `Delivery fee: $${dollars}`);
      feeRow.hidden = false;
    }
  }

  renderDropshipError() {
    this.setOption('delivery', false, 'Unable to check delivery. Please try again.');
    const feeRow = this.querySelector('[data-option="delivery-fee"]');
    if (feeRow) feeRow.hidden = true;
    const cta = this.querySelector('[data-dropship-cta]');
    if (cta) cta.hidden = true;
  }
}

customElements.define('postcode-checker', PostcodeChecker);
}
